import { useEffect, useMemo, useRef, useState } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
// @ts-expect-error — no types shipped, registered for cose-bilkent layout.
import coseBilkent from 'cytoscape-cose-bilkent';
import { useNavigate } from 'react-router-dom';

cytoscape.use(coseBilkent);
import { PageStage } from '../../components/PageStage';
import { SectionHead } from '../../components/SectionHead';
import { apiClient } from '../../lib/api';
import styles from './Navigator.module.css';

interface GraphNode {
  id: string;
  label: string;
  kind: 'partner' | 'related';
  intimacy?: number | null;
  importance?: number | null;
  temperature?: number | null;
  home_base?: string | null;
  relationship?: string | null;
  parent_id?: string | null;
}

interface GraphEdge {
  id: string;
  source: string;
  target: string;
  kind: 'kinship' | 'tag' | 'exchange';
  label?: string | null;
}

interface GraphResponse {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

const STYLE = [
  {
    selector: 'node[kind = "partner"]',
    css: {
      'background-color': '#8B2A2C',
      label: 'data(label)',
      'font-family': 'serif',
      'font-size': '13px',
      color: '#14110D',
      'text-valign': 'bottom',
      'text-margin-y': 6,
      'text-wrap': 'wrap',
      'text-max-width': '120px',
      width: 'data(size)',
      height: 'data(size)',
      'border-color': '#14110D',
      'border-width': 0.5,
    },
  },
  {
    selector: 'node[kind = "related"]',
    css: {
      'background-color': '#E5D6B5',
      'border-color': '#8E6526',
      'border-width': 1,
      label: 'data(label)',
      'font-family': 'sans-serif',
      'font-size': '10px',
      'font-style': 'italic',
      color: '#382E22',
      'text-valign': 'bottom',
      'text-margin-y': 4,
      width: 12,
      height: 12,
      shape: 'round-rectangle',
    },
  },
  {
    selector: 'edge[kind = "kinship"]',
    css: {
      'line-color': '#8E6526',
      'line-style': 'solid',
      width: 1,
      'curve-style': 'bezier',
      label: 'data(label)',
      'font-family': 'sans-serif',
      'font-size': '8px',
      color: '#6B5C46',
      'text-rotation': 'autorotate',
    },
  },
  {
    selector: 'edge[kind = "tag"]',
    css: {
      'line-color': '#C9A86A',
      'line-style': 'dashed',
      width: 0.5,
      'curve-style': 'bezier',
      opacity: 0.55,
    },
  },
  {
    selector: 'node[kind = "cluster"]',
    css: {
      'background-color': '#F6EFDE',
      'background-opacity': 0.4,
      'border-color': '#8E6526',
      'border-width': 0.5,
      'border-style': 'dashed',
      shape: 'round-rectangle',
      label: 'data(label)',
      'font-family': 'sans-serif',
      'font-size': '11px',
      'font-weight': 'bold',
      'text-transform': 'uppercase',
      'letter-spacing': '1.4px',
      color: '#8E6526',
      'text-valign': 'top',
      'text-halign': 'center',
      'text-margin-y': -4,
      padding: '12px',
    },
  },
  {
    selector: 'node[?structuralHole]',
    css: {
      'border-color': '#C9A86A',
      'border-width': 3,
      'border-style': 'solid',
      'box-shadow': '0 0 8px 3px rgba(201,168,106,0.55)',
    },
  },
  {
    selector: 'node:selected',
    css: {
      'border-color': '#14110D',
      'border-width': 2,
    },
  },
];

const LAYOUT = {
  name: 'cose-bilkent',
  animate: 'end',
  animationDuration: 500,
  padding: 50,
  randomize: true,
  nodeRepulsion: 4500,
  idealEdgeLength: 120,
  edgeElasticity: 0.45,
  gravity: 0.25,
  numIter: 2500,
  tile: true,
  tilingPaddingVertical: 10,
  tilingPaddingHorizontal: 10,
  nestingFactor: 0.1,
};

export function NavigatorPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<GraphResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<GraphNode | null>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);

  useEffect(() => {
    apiClient
      .get<GraphResponse>('/api/v1/crm/graph')
      .then(setData)
      .catch((e) => setError((e as Error)?.message || 'Не удалось загрузить граф'));
  }, []);

  const elements = useMemo(() => {
    if (!data) return [];
    const partnerNodes = data.nodes.filter((n) => n.kind === 'partner');
    const relatedNodes = data.nodes.filter((n) => n.kind === 'related');

    // Cluster: each function-tag becomes a compound parent. A partner with
    // multiple tags is grouped under its first tag (good enough for v1).
    const tagOfPartner = new Map<string, string | null>();
    for (const e of data.edges) {
      if (e.kind !== 'tag' || !e.label) continue;
      if (!tagOfPartner.has(e.source)) tagOfPartner.set(e.source, e.label);
      if (!tagOfPartner.has(e.target)) tagOfPartner.set(e.target, e.label);
    }
    const usedTags = new Set<string>();
    for (const v of tagOfPartner.values()) if (v) usedTags.add(v);

    const tagParentNodes = Array.from(usedTags).map((tag) => ({
      data: { id: `tag:${tag}`, label: tag, kind: 'cluster' },
    }));
    const partnerEls = partnerNodes.map((n) => {
      const parent = tagOfPartner.get(n.id) ?? null;
      return {
        data: {
          ...n,
          size: 18 + (n.importance ?? 1) * 4,
          ...(parent ? { parent: `tag:${parent}` } : {}),
        },
      };
    });
    const relatedEls = relatedNodes.map((n) => ({
      data: { ...n, size: 12 },
    }));
    // Drop tag-edges in favor of cluster grouping (less noise).
    const edges = data.edges
      .filter((e) => e.kind !== 'tag')
      .map((e) => ({ data: e }));
    return [...tagParentNodes, ...partnerEls, ...relatedEls, ...edges];
  }, [data]);

  const onCy = (cy: cytoscape.Core) => {
    cyRef.current = cy;
    cy.on('tap', 'node', (evt) => {
      const node = evt.target;
      setSelected(node.data() as GraphNode);
    });
    cy.on('dbltap', 'node[kind = "partner"]', (evt) => {
      const id = (evt.target.data() as GraphNode).id.replace(/^p:/, '');
      navigate(`/partners/${id}`);
    });
    // Structural holes: highlight top-N partners by betweenness centrality.
    // They are the unique bridges between otherwise-disconnected clusters.
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bc = (cy.elements() as any).betweennessCentrality({ directed: false }) as {
        betweennessNormalized: (node: cytoscape.NodeSingular) => number;
      };
      const partnerNodes = cy.nodes('[kind = "partner"]');
      if (partnerNodes.length > 2) {
        const scored = partnerNodes.map((n) => ({
          node: n,
          score: bc.betweennessNormalized(n),
        }));
        const threshold = scored.length > 5 ? 0.15 : 0.05;
        scored.forEach(({ node, score }) => {
          if (score >= threshold) {
            node.data('structuralHole', true);
          }
        });
      }
    } catch {
      // Betweenness centrality may fail on sparse graphs — silent.
    }
  };

  return (
    <PageStage>
      <SectionHead
        eyebrow="Навигатор"
        title="Граф"
        italic="сети"
        sub="Партнёры — кругами (размер ∝ важности), близкие — маленькими квадратами. Кластеры — common function-tag (private banking, family office, art и т.д.). Сплошные линии — кровные/деловые связи. Двойной клик по партнёру — открыть профиль."
      />
      {error && <div className={styles.error}>{error}</div>}
      {!data && !error && <div className={styles.note}>Грузим граф…</div>}
      {data && data.nodes.length === 0 && (
        <div className={styles.note}>Круг пуст — добавь первых партнёров.</div>
      )}
      {data && data.nodes.length > 0 && (
        <div className={styles.canvas}>
          <CytoscapeComponent
            elements={elements}
            stylesheet={STYLE as unknown as cytoscape.StylesheetCSS[]}
            layout={LAYOUT as unknown as cytoscape.LayoutOptions}
            style={{ width: '100%', height: '600px', background: 'var(--paper)' }}
            cy={onCy}
          />
          {selected && (
            <div className={styles.detail}>
              <div className={styles.detailLabel}>{selected.kind.toUpperCase()}</div>
              <div className={styles.detailName}>{selected.label}</div>
              {selected.kind === 'partner' && (
                <div className={styles.detailMeta}>
                  {selected.intimacy != null && <span>близость {selected.intimacy}/5</span>}
                  {selected.importance != null && <span>важность {selected.importance}/5</span>}
                  {selected.temperature != null && <span>T {selected.temperature}°</span>}
                  {selected.home_base && <span>{selected.home_base}</span>}
                </div>
              )}
              {selected.kind === 'related' && (
                <div className={styles.detailMeta}>{selected.relationship}</div>
              )}
            </div>
          )}
        </div>
      )}
      <p className={styles.note}>
        Партнёры — кругами (размер ∝ важности), близкие — квадратами, кластеры — function-tag.
        Золотая рамка = <strong>структурная дыра</strong> (Бёрт) — ты единственная связь между кластерами. Это твой ключевой актив.
        Двойной клик — профиль партнёра.
      </p>
    </PageStage>
  );
}
